import DeleteIcon from '@mui/icons-material/Delete';
import DynamicFeedIcon from '@mui/icons-material/DynamicFeed';
import EditIcon from '@mui/icons-material/Edit';
import MenuButton from '@mui/icons-material/MoreVert';
import ShortcutIcon from '@mui/icons-material/Shortcut';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import ButtonBase from '@mui/material/ButtonBase';
import { accountsActions, accountsSelectors, accountsTypes } from 'app-components/flux/accounts';
import { businessActions, businessSelectors, businessTypes } from 'app-components/flux/business';
import type { Business } from 'app-components/flux/business/businessTypes';
import { featureFlagsSelectors } from 'app-components/flux/feature-flags';
import { rootUiActions, rootUiTypes } from 'app-components/flux/root-ui';
import {
  DIALOG_TYPE as CONFIRMATION_DIALOG_TYPE,
  mkConfirmationDialogProps,
} from 'components/Dialogs/ConfirmationDialog';
import NestedQMenu from 'components/NestedQMenu';
import { useAppSelector } from 'hooks/redux';
import { Map as ImmutableMap } from 'immutable';
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()(({ palette, spacing }) => ({
  menu: {
    marginLeft: 8,
    borderRadius: '50%',
    '&:hover': {
      backgroundColor: palette.action.hover,
    },
  },
  icon: {
    color: palette.icon.main,
    marginRight: spacing(2),
  },
  closeIcon: {
    color: palette.amount.negative,
    marginRight: spacing(2),
  },
}));

type Props = {
  business: Business;
  setEditBusiness: (value: boolean) => void;
};

export default function BusinessThreeDot(props: Props) {
  const { business, setEditBusiness } = props;
  const { classes } = useStyles();
  const businessId = business.id;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLButtonElement>(null);
  const defaultBusiness = useAppSelector(businessSelectors.getDefaultBusiness);
  const nonDefaultBusinesses = useAppSelector(businessSelectors.getNonDefaultBusinesses);

  const isDefaultBusiness = business.id === defaultBusiness?.id;

  const accountsById = useAppSelector(accountsSelectors.getAccountsById);

  // @ts-expect-error FIXME TS7006
  const buildBusinessUpdate = (biz, isClosed, isDefault) =>
    biz.has('isDefault')
      ? biz.merge({ isClosed, isDefault })
      : businessTypes.mkBusiness({ ...biz?.toJS(), isDefault, isClosed });

  // @ts-expect-error FIXME TS7006
  const updatePrimary = (newPrimary, shouldCloseDefault = false) => {
    const newPrimaryModified = buildBusinessUpdate(newPrimary, false, true);
    if (defaultBusiness) {
      const oldPrimary = buildBusinessUpdate(defaultBusiness, shouldCloseDefault, false);
      dispatch(businessActions.batchUpdateBusinesses([newPrimaryModified, oldPrimary]));
    } else {
      dispatch(businessActions.updateBusiness(newPrimaryModified));
    }
  };
  const showCloseBusiness = useAppSelector((state) => featureFlagsSelectors.getFeatureFlag(state, 'closedBusiness'));

  const menuOptions = !business?.isClosed
    ? [
        {
          id: 'edit-biz-accounts',
          onSelect: () => {
            setEditBusiness(true);
            setAnchorEl(null);
          },
          startIcon: <EditIcon className={classes.icon} />,
          label: 'Edit business',
          value: 'edit-biz-accounts',
          divider: true,
        },
        {
          id: 'manage-business-accts',
          onSelect: () => navigate('/settings/accounts', { replace: true }),
          startIcon: <ShortcutIcon className={classes.icon} />,
          label: 'Manage business accounts',
          value: 'manage-business-accts',
          divider: true,
        },
        {
          id: 'update-to-primary',
          label: 'Set as primary business',
          value: 'update-to-primary',
          startIcon: <SwapVertIcon className={classes.icon} />,
          divider: !isDefaultBusiness && showCloseBusiness,
          nop: isDefaultBusiness,
          tooltip: {
            text: 'Updating your primary business will change all of your report views to show this business first.',
          },
          onSelect: () => {
            updatePrimary(business);
            setAnchorEl(null);
          },
        },
        ...(showCloseBusiness
          ? [
              {
                id: 'close-biz',
                onSelect: () => {
                  setAnchorEl(null);
                  dispatch(
                    rootUiActions.createDialog(
                      rootUiTypes.mkRootUiData({
                        id: 'close-business',
                        type: CONFIRMATION_DIALOG_TYPE,
                        allowNesting: true,
                        props: ImmutableMap(
                          mkConfirmationDialogProps({
                            title: 'Close business',
                            content: (
                              <span>
                                Are you sure you sure you want to close {business?.name}? <br /> <br />
                                While your existing transactions will be preserved and linked with this business, no new
                                transactions will be associated with this business. <br /> <br />
                                All associated accounts will have their primary usage set to Personal.
                              </span>
                            ),
                            confirmLabel: 'Confirm',
                            denyLabel: 'Cancel',
                            confirmAction: () => {
                              accountsById?.forEach((account) => {
                                if (account.businessId === businessId) {
                                  dispatch(
                                    accountsActions.updateAccount(
                                      accountsTypes.mkUpdateAccount(account.type, {
                                        id: account.id,
                                        usageType: 'PERSONAL',
                                        businessId: '0',
                                      }),
                                    ),
                                  );
                                }
                              });
                              if (isDefaultBusiness && nonDefaultBusinesses && nonDefaultBusinesses?.size > 0) {
                                updatePrimary(nonDefaultBusinesses.first(), true);
                              } else {
                                dispatch(businessActions.updateBusiness(buildBusinessUpdate(business, true, false)));
                              }
                            },
                          }),
                        ),
                      }),
                    ),
                  );
                },
                startIcon: <DeleteIcon className={classes.closeIcon} />, // TODO: Should this be changed to the new 'CLOSE' icon we are using?
                label: 'Close business',
                value: 'close-biz',
                divider: false,
              },
            ]
          : []),
      ]
    : [
        ...(showCloseBusiness
          ? [
              {
                id: 'reopen-biz',
                label: 'Reopen business',
                onSelect: () => {
                  setAnchorEl(null);
                  dispatch(
                    rootUiActions.createDialog(
                      rootUiTypes.mkRootUiData({
                        id: 'reopen-business',
                        type: CONFIRMATION_DIALOG_TYPE,
                        allowNesting: true,
                        props: ImmutableMap(
                          mkConfirmationDialogProps({
                            title: 'Reopen business',
                            content: (
                              <span>
                                Are you sure you sure you want to reopen {business?.name}? <br /> <br />
                                When reopened, go to the Accounts page in Settings to choose the accounts you’d like to
                                associate with the business.
                              </span>
                            ),
                            confirmLabel: 'Confirm',
                            denyLabel: 'Cancel',
                            confirmAction: () => {
                              if (nonDefaultBusinesses?.size === 0 || nonDefaultBusinesses === null) {
                                dispatch(businessActions.updateBusiness(buildBusinessUpdate(business, false, true)));
                              } else {
                                dispatch(businessActions.updateBusiness(business.set('isClosed', false)));
                              }
                            },
                          }),
                        ),
                      }),
                    ),
                  );
                },
                startIcon: <DynamicFeedIcon className={classes.icon} />,
                divider: true,
                value: 'reopen-biz',
              },
            ]
          : []),
        {
          id: 'edit-biz-accounts',
          onSelect: () => {
            setEditBusiness(true);
            setAnchorEl(null);
          },
          startIcon: <EditIcon className={classes.icon} />,
          label: 'Edit business',
          value: 'edit-biz-accounts',
          divider: showCloseBusiness,
        },
        ...(showCloseBusiness
          ? [
              {
                id: 'delete-biz',
                onSelect: () => {
                  setAnchorEl(null);
                  dispatch(
                    rootUiActions.createDialog(
                      rootUiTypes.mkRootUiData({
                        id: 'delete-business',
                        type: CONFIRMATION_DIALOG_TYPE,
                        allowNesting: true,
                        props: ImmutableMap(
                          mkConfirmationDialogProps({
                            title: 'Delete business',
                            content: <span>Are you sure you sure you want to delete {business?.name}?</span>,
                            confirmLabel: 'Confirm',
                            denyLabel: 'Cancel',
                            confirmAction: () => {
                              dispatch(businessActions.deleteBusiness(business));
                            },
                          }),
                        ),
                      }),
                    ),
                  );
                },
                startIcon: <DeleteIcon className={classes.closeIcon} />,
                label: 'Delete business',
                value: 'delete-biz',
              },
            ]
          : []),
      ];

  return (
    <>
      <NestedQMenu
        id='business-three-dot-menu'
        key={`three-dot-${businessId}`}
        anchorEl={anchorEl}
        onClose={() => {
          setAnchorEl(null);
        }}
        open={Boolean(anchorEl)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        options={menuOptions}
      />
      <ButtonBase
        className={classes.menu}
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          setAnchorEl(e.currentTarget);
        }}
        id={`business-three-dot-menu-${businessId}`}
        key={`business-three-dot-menu-${businessId}`}
      >
        <MenuButton />
      </ButtonBase>
    </>
  );
}
